import { Mutation } from "@apollo/client/react/components";
import React, { useState } from "react";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { Button } from "@govlaunch/core";
import { Hover } from "react-powerplug";
import AcceptProductStackRequest from "~/lib/mutations/AcceptProductStackRequest";
import {
  IAcceptProductStackRequestMutation,
  IAcceptProductStackRequestMutationVariables,
} from "~/lib/mutations/__generated__/AcceptProductStackRequest.generated";
import DeclineProductStackRequest from "~/lib/mutations/DeclineProductStackRequest";
import {
  IDeclineProductStackRequestMutation,
  IDeclineProductStackRequestMutationVariables,
} from "~/lib/mutations/__generated__/DeclineProductStackRequest.generated";
import CheckIcon from "~/components/icons/CheckIcon";
import CloseIcon from "~/components/icons/CloseIcon";

import DismissActionCenter from "~/lib/mutations/DismissActionCenter";
import { Margin } from "~/components/spacings";
import {
  IDismissActionCenterMutation,
  IDismissActionCenterMutationVariables,
} from "~/lib/mutations/__generated__/DismissActionCenter.generated";
import {
  IVendorUser,
  IGovernmentUser,
  IPendingUser,
  IProductStackRequest,
  ICompany,
  IProduct,
} from "~/types/types";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import { FormattedMessage, useIntl } from "react-intl";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import Link from "next/link";

type Inviter =
  | Pick<IVendorUser, "__typename" | "fullName" | "slug">
  | Pick<IGovernmentUser, "__typename" | "fullName" | "slug">
  | Pick<IPendingUser, "__typename" | "fullName" | "slug">;

interface IProductStackRequestActionProps {
  action: Pick<IProductStackRequest, "_id" | "createdAt"> & {
    product: Pick<IProduct, "_id" | "name" | "logo" | "slug">;
  } & { vendor: Pick<ICompany, "_id" | "name" | "logo" | "slug"> } & {
    inviter: Inviter;
  };
}

function ProductStackRequestAction({ action }: IProductStackRequestActionProps) {
  const intl = useIntl();
  const [acceptedProduct, setAcceptedProduct] = useState(false);
  const [declinedProduct, setDeclinedProduct] = useState(false);

  return (
    <Mutation<IDismissActionCenterMutation, IDismissActionCenterMutationVariables> mutation={DismissActionCenter}>
      {(dismissActionCenter) => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            padding: 12,
            backgroundColor: palette.white,
            ":hover": {
              backgroundColor: palette.washGray,
            },
          }}
        >
          <ProfilePicture size={46} image={action.product.logo} name={action.product.name} />

          <Margin ml={12} mr="auto">
            <p
              css={{
                fontSize: 14,
                color: palette.mediumGray,
                margin: 0,
                lineHeight: "18px",
              }}
            >
              <Link passHref={true} href="/[userSlug]/[[...tab]]" as={`/@${action.inviter.slug}`}>
                <a
                  css={{
                    color: palette.primary,
                    fontWeight: 600,
                    ":hover": {
                      textDecoration: "underline !important",
                    },
                  }}
                >
                  {action.inviter.fullName}
                </a>
              </Link>{" "}
              <FormattedMessage
                defaultMessage="invited your government to add {productName} from vendor {vendorName}" id="9pW94p"
                values={{
                  productName: action.product.name,
                  vendorName: action.vendor.name,
                }}
              />{" "}
              <span
                css={{
                  color: palette.sealBlue,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                <IntlRelativeTime value={action.createdAt} />
              </span>
            </p>
          </Margin>

          <div
            css={{
              display: "flex",
              width: 170,
              minWidth: 170,
              justifyContent: "flex-end",
            }}
          >
            {acceptedProduct && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.greenBlue,
                  }}
                >
                  <CheckIcon />
                  <Margin ml={6}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Added" id="rIAJlE" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {declinedProduct && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.red,
                  }}
                >
                  <CloseIcon fill={palette.red} variation="thick" width={15} height={15} />
                  <Margin ml={8}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Declined" id="uRYIb9" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {!acceptedProduct && !declinedProduct && (
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  alignItems: "center",
                  gridColumnGap: 12,
                }}
              >
                <Mutation<IAcceptProductStackRequestMutation, IAcceptProductStackRequestMutationVariables>
                  mutation={AcceptProductStackRequest}
                  refetchQueries={refetchQueries(["Selfie", "Government"])}
                >
                  {(acceptProductStackRequest) => (
                    <Hover>
                      {({ bind, hovered }) => (
                        <Button
                          theme={hovered ? "primary" : "secondary"}
                          size="xsmall"
                          color={palette.innovatorBlue}
                          style={{
                            opacity: 1,
                            backgroundColor: hovered ? palette.innovatorBlue : palette.white,
                            width: 100,
                          }}
                          {...bind}
                          onClick={() => {
                            acceptProductStackRequest({
                              variables: {
                                requestId: action._id,
                              },
                            });
                            dismissActionCenter();
                            setAcceptedProduct(true);
                          }}
                        >
                          <span
                            css={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FormattedMessage defaultMessage="Verify Product" id="Q5gWEo" />
                          </span>
                        </Button>
                      )}
                    </Hover>
                  )}
                </Mutation>

                <Mutation<IDeclineProductStackRequestMutation, IDeclineProductStackRequestMutationVariables>
                  mutation={DeclineProductStackRequest}
                >
                  {(declineProductStackRequest) => (
                    <span
                      css={{
                        cursor: "pointer",
                        fontSize: 12,
                        color: palette.red,
                        ":hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        const message = intl.formatMessage(
                          {
                            defaultMessage: 'Are you sure you want to decline the invite to {productName}?', id: 'ST82Cr',
                          },
                          {
                            productName: action.product.name,
                          },
                        );

                        if (window.confirm(message)) {
                          declineProductStackRequest({
                            variables: {
                              requestId: action._id,
                            },
                          });
                          dismissActionCenter();
                          setDeclinedProduct(true);
                        }
                      }}
                    >
                      <FormattedMessage id="pvtgR2" defaultMessage="Decline" />
                    </span>
                  )}
                </Mutation>
              </div>
            )}
          </div>
        </div>
      )}
    </Mutation>
  );
}

export default ProductStackRequestAction;
